import { useEffect, useState } from 'react';

import { Cancel01Icon, HeadsetIcon, Logout01Icon, ReloadIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { AdminPermissions } from 'constants/admin-permissions';
import { useAccessControl } from 'hooks/access-control';
import { useDocumentTitle } from 'hooks/document-title';
import { useToast } from 'hooks/toast';
import AdminPage from 'layouts/AdminPage/AdminPage';
import Moment from 'react-moment';
import { useSelector } from 'react-redux';

import { NewSupportAccessModal } from './components/NewSupportAccessModal/NewSupportAccessModal';
import { IconButton } from 'components/IconButton/IconButton';
import { Table } from 'components/Table/Table';

import { selectAuthentication } from 'api/authentication-slice';
import { useGetAllCompaniesListQuery } from 'api/company-api';
import {
  useCancelSupportAccessRequestMutation,
  useCompleteSupportAccessRequestMutation,
  useLazyGetSupportAccessRequestsQuery,
} from 'api/support-access-api';

import styles from './AdminSupportAccess.module.scss';

const STATUS_CLASS = {
  pending: 'u-status-pending',
  active: 'u-status-completed',
  expired: 'u-status-canceled',
  denied: 'u-status-declined',
  revoked: 'u-status-declined',
  completed: 'u-status-completed',
};

const AdminSupportAccess = () => {
  useDocumentTitle('Support Access');

  const showToast = useToast();
  const authentication = useSelector(selectAuthentication);
  const hasPermission = useAccessControl(authentication.admin_permissions);

  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [companiesOptions, setCompaniesOptions] = useState([]);
  const [reRequestDefaults, setReRequestDefaults] = useState(null);

  const [getSupportAccessRequests] = useLazyGetSupportAccessRequestsQuery();
  const [cancelRequest] = useCancelSupportAccessRequestMutation();
  const [completeRequest] = useCompleteSupportAccessRequestMutation();
  const { data: companiesData } = useGetAllCompaniesListQuery();

  useEffect(() => {
    if (!companiesData) return;

    setCompaniesOptions([
      { name: 'All Companies', value: 'all' },
      ...companiesData.result.map(company => ({ name: company.name, value: company.id })),
    ]);
  }, [companiesData]);

  const fetchRequests = async (query = {}) => {
    setLoading(true);
    const result = await getSupportAccessRequests(query).unwrap();
    setRequests(result);
    setLoading(false);
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleCancel = async id => {
    setLoading(true);
    try {
      await cancelRequest(id).unwrap();
      await fetchRequests();
      showToast('Request Cancelled', 'The support access request has been cancelled.', 'success');
    } catch {
      showToast('Error', 'Failed to cancel the request.', 'error');
      setLoading(false);
    }
  };

  const handleCreate = async request => {
    setShowNewModal(false);
    setRequests(prev => [request, ...prev]);
    showToast('Request Sent', 'Support access request created and sent to the client.', 'success');
  };

  const handleComplete = async id => {
    setLoading(true);
    try {
      await completeRequest(id).unwrap();
      await fetchRequests();
      showToast('Access Ended', 'The support access has been marked as completed.', 'success');
    } catch {
      showToast('Error', 'Failed to end the support access.', 'error');
      setLoading(false);
    }
  };

  const handleReRequest = request => {
    setReRequestDefaults({
      company_id: request.company_id,
      reason: request.reason,
      ticket_number: request.ticket_number,
      duration_hours: request.duration_hours,
    });
    setShowNewModal(true);
  };

  return (
    <AdminPage
      loading={loading}
      title="Support Access"
      button={hasPermission(AdminPermissions.LoginAsClient)}
      buttonText={
        <span className="inline-flex items-center gap-1.5 text-white">
          <HugeiconsIcon icon={HeadsetIcon} size={16} strokeWidth={2} color="currentColor" />
          New Support Access
        </span>
      }
      onClick={() => {
        setReRequestDefaults(null);
        setShowNewModal(true);
      }}
      allowedRole={AdminPermissions.LoginAsClient}>
      <NewSupportAccessModal
        visible={showNewModal}
        initialValues={reRequestDefaults}
        onClose={() => setShowNewModal(false)}
        onSubmit={handleCreate}
      />

      <Table.Context onQueryUpdate={fetchRequests}>
        <Table.FloatingFilters className="u-margin--large">
          <Table.FloatingFiltersLeft>
            <Table.MultiSelectFilter
              title="Status"
              filterName="status"
              type="status"
              useSearchInMultiSelect={false}
              options={[
                { value: '0', name: 'Pending' },
                { value: '1', name: 'Active' },
                { value: '2', name: 'Expired' },
                { value: '3', name: 'Denied' },
                { value: '4', name: 'Revoked' },
                { value: '5', name: 'Completed' },
              ]}
            />
            <Table.Filter
              title="Company"
              filterName="company_id"
              useDefault={false}
              useSearch
              options={companiesOptions}
            />
            <Table.DateRangeFilter title="Requested on" filterName="created_at" />
          </Table.FloatingFiltersLeft>
        </Table.FloatingFilters>
        <Table.TableContent className="u-margin--large">
          <Table.Filters>
            <Table.LeftFilters>
              <Table.Search placeholder="Search by reason or ticket number..." />
            </Table.LeftFilters>
          </Table.Filters>
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Header>Company</Table.Header>
                <Table.Header>Requested By</Table.Header>
                <Table.Header>Reason</Table.Header>
                <Table.Header>Ticket</Table.Header>
                <Table.Header>Requested</Table.Header>
                <Table.Header>Duration</Table.Header>
                <Table.Header>Status</Table.Header>
                <Table.Header>Expires</Table.Header>
                <Table.Header>Actions</Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {requests.map(request => (
                <Table.Row key={request.id}>
                  <Table.Col>{request.company_name}</Table.Col>
                  <Table.Col>{request.requested_by_name}</Table.Col>
                  <Table.Col>{request.reason}</Table.Col>
                  <Table.Col>{request.ticket_number || '—'}</Table.Col>
                  <Table.Col>
                    <Moment format="DD/MM/YYYY HH:mm">{request.created_at}</Moment>
                  </Table.Col>
                  <Table.Col>{request.duration_hours}h</Table.Col>
                  <Table.Col>
                    <span className={cn(styles.statusPill, STATUS_CLASS[request.status])}>
                      {request.status}
                    </span>
                  </Table.Col>
                  <Table.Col>
                    {request.expires_at ? (
                      <Moment format="DD/MM/YYYY HH:mm">{request.expires_at}</Moment>
                    ) : (
                      '—'
                    )}
                  </Table.Col>
                  <Table.Col>
                    {request.status === 'pending' && (
                      <IconButton
                        icon={Cancel01Icon}
                        prefix="hugeicons"
                        tip="Cancel"
                        onClick={() => handleCancel(request.id)}
                      />
                    )}
                    {['expired', 'denied', 'revoked', 'completed'].includes(request.status) && (
                      <IconButton
                        icon={ReloadIcon}
                        prefix="hugeicons"
                        tip="Re-request"
                        onClick={() => handleReRequest(request)}
                      />
                    )}
                    {request.status === 'active' && (
                      <IconButton
                        icon={Logout01Icon}
                        prefix="hugeicons"
                        tip="Exit"
                        onClick={() => handleComplete(request.id)}
                      />
                    )}
                  </Table.Col>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Table.TableContent>
      </Table.Context>
    </AdminPage>
  );
};

export default AdminSupportAccess;
